import { z } from 'zod';
import { addressLocationSchema } from './address.schema';
import { objectIdSchema } from '../validation/id';

/**
 * Denormalized city reference for a last-known-location snapshot.
 *
 * We intentionally denormalize `name` here — the standard platform
 * convention is to store only `customCode` and resolve the display
 * name via the region-translate cache at render time. That works
 * for UI-heavy paths where a component like `<RegionName />` can
 * fan out to a batch translator. Agent tools and the fleet god-view
 * both read `Driver.meta.lastKnownLocation.city.name` on the hot
 * path — they cannot afford a translation round-trip per row.
 *
 * Fields:
 * - `name` — display name for the city (e.g. "Riyadh" / "الرياض").
 *   Denormalized so agent tools / god-view can render synchronously.
 * - `code` — the platform's `customCode` (e.g. "riyadh"). Optional
 *   because IP / user-stated / phone-country writers may know a name
 *   without a canonical code.
 * - `regionId` — the canonical `Region._id` when available. Lets
 *   callers hop to the full `Region` doc for hierarchy / boundaries
 *   without an extra name-based lookup.
 */
export const cityRefSchema = z.object({
  /** Display name for the city; e.g. "Riyadh" / "الرياض". */
  name: z.string().trim(),
  /** Canonical `Region.customCode` (e.g. "riyadh"). Optional. */
  code: z.string().trim().optional(),
  /** Optional link back to the canonical `Region` document. */
  regionId: objectIdSchema.optional(),
});
export type CityRef = z.infer<typeof cityRefSchema>;

/**
 * Source of a location snapshot. Ordered from highest fidelity
 * (`iot_gps` / `app_*`) to lowest (`phone_country` / `saved_location_default`).
 *
 * Writers must never overwrite a snapshot from a higher-tier source
 * with a lower-tier source — the two tiers of `UserMetaLocation`
 * (`accurate` vs `approximate`) exist to keep the split explicit.
 */
export const lastKnownLocationSourceSchema = z.enum([
  /** Foreground app GPS ping (highest fidelity — user is looking at the app). */
  'app_foreground',
  /** Background app GPS ping (still high fidelity — permissioned). */
  'app_background',
  /** Vehicle IoT / hardware GPS tracker (highest fidelity for a Vehicle). */
  'iot_gps',
  /**
   * Passive read from the `user-location` header that every SDK
   * attaches to every authed request. GPS-quality (`accuracy: 'gps'`)
   * but distinctly lower tier than the `app_*` sources — the
   * client cached the coord opportunistically, then reused it for N
   * requests, so it can be seconds-to-minutes stale relative to
   * where the user *actually* is right now. The backend
   * `PresenceInterceptor` filters out stale + coarse header reads
   * before promoting them to a `lastKnownLocation` snapshot. See
   * `docs/ai/user-signals.md` "Passive location fan-out".
   */
  'app_passive',
  /**
   * Location pin (or parsed Google Maps link) the person deliberately
   * shared over WhatsApp — the app-less-driver tracking path (see
   * `docs/ai/whatsapp-agent.md`). GPS-quality, but the pin reflects
   * "where I chose to drop the marker", so it ranks below the app_*
   * sources and above `app_passive`.
   */
  'whatsapp',
  /** Reverse-geocoded from the request IP address. Country-level accuracy. */
  'ip',
  /** User stated their location in chat / a form ("I'm in Riyadh"). City-level. */
  'user_stated',
  /** Derived from the phone-number country prefix. Country-level. */
  'phone_country',
  /** Default city / region from a `SavedLocation` (home / warehouse). City-level. */
  'saved_location_default',
]);
export type LastKnownLocationSource = z.infer<typeof lastKnownLocationSourceSchema>;

/**
 * Tier ordering of location sources — used by
 * `UserMetaService.fanOutAccurateLocation` and
 * `recordPassiveLocation` to decide whether a new snapshot should
 * overwrite an existing one. Higher number = higher fidelity /
 * more trustworthy.
 *
 * Ordering rationale:
 * - `iot_gps` (100): dedicated hardware, sub-metre precision, no
 *   dependency on a phone being unlocked.
 * - `app_background` (90): OS-permissioned GPS from a listening
 *   RN service (Transistor). Higher than foreground because it's
 *   the "the driver is actively moving" tier.
 * - `app_foreground` (80): explicit `Geolocation.getCurrentPosition`
 *   call from the app.
 * - `app_passive` (50): opportunistic header-cached read. Same
 *   accuracy class as foreground but treated as lower tier so a
 *   fresh Transistor `app_background` ping doesn't get clobbered
 *   by a slightly-newer-but-passively-cached header snapshot.
 * - `user_stated` (30): the user said so in chat/form. City-level.
 * - `saved_location_default` (25): fallback from a saved place.
 *   City-level.
 * - `ip` (20): backend IP-geolocation.
 * - `phone_country` (10): derived from E.164 prefix. Country-level.
 *
 * A new snapshot always overwrites the previous one UNLESS the
 * previous one is both **higher tier** AND **still fresh** (see the
 * `FRESH_TIER_HOLD_MS` constant on `UserMetaService`).
 */
export const LAST_KNOWN_LOCATION_SOURCE_TIER: Record<LastKnownLocationSource, number> = {
  iot_gps: 100,
  app_background: 90,
  app_foreground: 80,
  whatsapp: 70,
  app_passive: 50,
  user_stated: 30,
  saved_location_default: 25,
  ip: 20,
  phone_country: 10,
};

/**
 * Coarse accuracy tag for a location snapshot. Kept separate from
 * `source` because two sources can share the same accuracy tier
 * (e.g. `user_stated` and `saved_location_default` are both `city`)
 * but consumers may still want to filter on the tier alone.
 */
export const lastKnownLocationAccuracySchema = z.enum([
  /** GPS point — accurate to metres. */
  'gps',
  /** City-level (a single point somewhere inside the city). */
  'city',
  /** Region / province-level. */
  'region',
  /** Country-level (usually the country capital). */
  'country',
]);
export type LastKnownLocationAccuracy = z.infer<typeof lastKnownLocationAccuracySchema>;

/**
 * A single last-known-location snapshot. Shared between:
 * - `User.meta.location.accurate` / `User.meta.location.approximate`
 * - `Driver.meta.lastKnownLocation`
 * - `Vehicle.meta.lastKnownLocation`
 *
 * The same shape is intentional — a mobile ping fans out to all
 * three collections, and the fleet god-view / agent tools read the
 * same fields regardless of which document they landed on.
 *
 * `point` uses the canonical Mongo GeoJSON format
 * (`[lng, lat]`) — see {@link addressLocationSchema}. Kept optional
 * because a snapshot from `phone_country` can arrive with a `city`
 * label but no meaningful lat/lng.
 */
export const lastKnownLocationSchema = z.object({
  /** GeoJSON Point `[lng, lat]`. Optional for country-level snapshots. */
  point: addressLocationSchema.optional(),
  /** When this snapshot was captured. */
  capturedAt: z.date(),
  /** Where the snapshot came from. Drives the accurate vs approximate split. */
  source: lastKnownLocationSourceSchema,
  /** Coarse accuracy tier. Redundant with `source` but useful for filters. */
  accuracy: lastKnownLocationAccuracySchema,
  /** Denormalized city ref — see {@link cityRefSchema}. */
  city: cityRefSchema.optional(),
  /** GPS accuracy in metres. Populated only when `source` is app_* / iot_gps. */
  accuracyMeters: z.number().optional(),
  /** Instantaneous speed in m/s. Populated only for GPS snapshots. */
  speed: z.number().optional(),
  /** Heading (bearing) in degrees, 0–359. Populated only for GPS snapshots. */
  heading: z.number().optional(),
  /**
   * Free-form note. Used mostly for `user_stated` (e.g. "user said
   * 'I'm in Al Khobar'") so an agent can quote the exact phrase back
   * to the user without re-reading the conversation transcript.
   */
  note: z.string().optional(),
});
export type LastKnownLocation = z.infer<typeof lastKnownLocationSchema>;
